// New and edit quote, and the new client box the form and New job share.
import {
  addDays,
  CLIENT_TITLES,
  type ClientDetail,
  type HomeAnswers,
  homeAnswersShape,
  type LawnAnswers,
  lawnAnswersShape,
  PRICE_OFTEN_WORDS,
  type PriceOften,
  type QuickQuote,
  type QuoteDetail,
  quoteTotals,
  splitAddress,
} from "@steward/shared";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { type SyntheticEvent, useState } from "react";
import { useClient, useClients, useCreateClient } from "../../api/hooks";
import { useCreateQuote, usePipeline, useQuote, useUpdateQuote } from "../../api/money";
import { useSession } from "../../app/session";
import { Choice, Combobox, FormField, InputText } from "../../components/fields";
import { moneyExact, phoneText, shortDate, todayIn } from "../../components/format";
import { Modal } from "../../components/overlays";
import { Banner, Button, Card, LoadError, Page, Skeleton } from "../../components/parts";
import { draftLine, type DraftLine, draftTotal, errText, LinesEditor, linesOut } from "../../components/work";
import { EMPTY_PLACE, newClientMissing, type PlaceDraft, PlaceFields, placeProperty, propertyOut } from "../clients/Place";
import { LawnQuickPrice, QuickPrice } from "../QuickPrice";
import { DAY, sourceWords } from "./common";
import { leadPlace } from "./Pipeline";

interface QuoteDraftLine extends DraftLine {
  optional: boolean;
}

/** The picker's last choice: opens the New client box. */
export const NEW_CLIENT = "new";

export const OFTEN_OF: Record<string, PriceOften> = {
  "one-time": "once",
  weekly: "weekly",
  biweekly: "biweekly",
  monthly: "monthly",
};

/** A quote this form can change: no Good, Better, Best options or monthly retainers it would drop. */
export const formCanEdit = (q: QuoteDetail) =>
  !q.proposal && (q.status === "draft" || q.status === "sent") && q.lines.every((l) => !l.optionGroup && l.bill === "once");

/** New quote, or Edit with quoteId (Edit only for quotes formCanEdit allows). */
export function QuoteForm({ leadId, clientId, quoteId }: { leadId?: string; clientId?: string; quoteId?: string }) {
  const existing = useQuote(quoteId ?? "", Boolean(quoteId));
  if (quoteId && !existing.data)
    return <Page title="Edit quote">{existing.error ? <LoadError error={existing.error} /> : <Skeleton rows={6} />}</Page>;
  // A proposal edits on its own page; a quote this form would break (options, retainers, signed) opens to read.
  if (existing.data?.proposal) return <Navigate to="/proposals/$id" params={{ id: existing.data.id }} replace />;
  if (existing.data && !formCanEdit(existing.data))
    return <Navigate to="/quotes/$id" params={{ id: existing.data.id }} replace />;
  return (
    <QuoteFormBody
      {...(leadId ? { leadId } : {})}
      {...(clientId ? { clientId } : {})}
      {...(quoteId && existing.data ? { q: existing.data } : {})}
    />
  );
}

function QuoteFormBody({ leadId, clientId, q }: { leadId?: string; clientId?: string; q?: QuoteDetail }) {
  const { company } = useSession();
  const navigate = useNavigate();
  const today = todayIn(company.timezone);
  const clients = useClients(company.id, { status: "active", sort: "last_activity" });
  const pipe = usePipeline(company.id);
  const lead = pipe.data?.leads.find((l) => l.id === leadId);
  const create = useCreateQuote();
  const update = useUpdateQuote();
  const [who, setWho] = useState(q?.clientId ?? clientId ?? "");
  const picked = useClient(who);
  const [newClient, setNewClient] = useState<NewClientStart | null>(null);
  const [title, setTitle] = useState(q?.title ?? "");
  const [message, setMessage] = useState(q?.message ?? "");
  const [frequency, setFrequency] = useState(q?.frequency ?? "one-time");
  const [lines, setLines] = useState<QuoteDraftLine[]>(() =>
    q
      ? q.lines.map((l) => ({
          ...draftLine({
            description: l.description,
            quantity: l.quantity,
            unitPriceCents: l.unitPriceCents,
            note: l.note,
            link: l.link ?? null,
            frequency: "every",
          }),
          frequency: q.frequency !== "one-time" && !l.recurring ? "first" : "every",
          optional: l.optional,
        }))
      : [{ ...draftLine(), optional: false }],
  );
  const [dep, setDep] = useState<"none" | "percent" | "amount">(q?.deposit?.kind ?? "none");
  const [depValue, setDepValue] = useState(
    q?.deposit ? String(q.deposit.kind === "amount" ? q.deposit.value / 100 : q.deposit.value) : "",
  );
  const [goodFor, setGoodFor] = useState(
    q?.expiresOn ? String(Math.max(1, Math.round((Date.parse(q.expiresOn) - Date.parse(today)) / DAY))) : "30",
  );
  const [err, setErr] = useState<string | null>(null);
  const [filled, setFilled] = useState(false);
  // From a lead: a client already made for it is picked; otherwise the New client box opens with what they sent.
  if (lead && !filled && !q) {
    setFilled(true);
    setTitle(lead.service ?? "");
    if (lead.clientId) setWho(lead.clientId);
    else {
      const [first = "", ...rest] = lead.name.trim().split(/\s+/);
      setNewClient({
        firstName: first,
        lastName: rest.join(" "),
        phone: phoneText(lead.phone) ?? "",
        email: lead.email ?? "",
        ...leadPlace(lead),
        source: sourceWords(lead.source) ?? "",
      });
    }
  }
  const repeating = frequency !== "one-time";
  const required = lines.filter((l) => !l.optional);
  const extras = lines.filter((l) => l.optional);
  const firstPlace = picked.data?.properties[0];
  const size = firstPlace ? { beds: firstPlace.beds, baths: firstPlace.baths, sqft: firstPlace.sqft } : undefined;
  // Priced before (the website or Price it fast): Price it fast opens with the same answers, grass height and all.
  const savedLawn = lawnAnswersShape.safeParse(q?.answers).data;
  const savedHome = savedLawn ? undefined : homeAnswersShape.safeParse(q?.answers).data;
  const [answers, setAnswers] = useState<HomeAnswers | LawnAnswers | null>(savedLawn ?? savedHome ?? null);
  const makeContactClient = () => {
    if (!q) return;
    const [first = "", ...rest] = (q.contactName ?? q.name).trim().split(/\s+/);
    const p = splitAddress(q.address);
    setNewClient({
      firstName: first,
      lastName: rest.join(" "),
      phone: phoneText(q.contactPhone) ?? "",
      email: q.contactEmail ?? "",
      street: p.street1 ?? q.address ?? "",
      street2: p.street2 ?? "",
      ...(p.city ? { city: p.city } : {}),
      ...(p.state ? { state: p.state } : {}),
      zip: p.zip ?? "",
    });
  };
  const applyQuick = (quick: QuickQuote, a: HomeAnswers | LawnAnswers | null) => {
    setAnswers(a);
    setTitle(quick.title);
    setFrequency(quick.frequency);
    setLines([
      ...quick.lines.map((l) => ({
        ...draftLine({
          description: l.description,
          quantity: 1,
          unitPriceCents: l.unitPriceCents,
          frequency: "every",
          note: l.note ?? null,
          link: l.link ?? null,
        }),
        frequency: l.recurring || quick.frequency === "one-time" ? "every" : "first",
        optional: false,
      })),
      ...extras,
    ]);
    setErr(null);
  };
  const save = () => {
    const out = linesOut(lines);
    if (typeof out === "string") {
      setErr(out);
      return;
    }
    if (!out.length) {
      setErr("A quote needs a line.");
      return;
    }
    if (!who && !q) {
      setErr("Pick a client, or make a new one at the bottom of the list.");
      return;
    }
    if (dep !== "none" && !(Number(depValue) > 0)) {
      setErr("Type the deposit, or pick No deposit.");
      return;
    }
    const days = Number(goodFor);
    if (goodFor.trim() && !(Number.isInteger(days) && days > 0 && days <= 365)) {
      setErr("Good for takes a number of days from 1 to 365.");
      return;
    }
    const kept = lines.filter((l) => l.description.trim() || l.price.trim());
    const body = {
      ...(who ? { clientId: who } : {}),
      title: title.trim() || null,
      message: message.trim() || null,
      frequency,
      expiresOn: goodFor.trim() ? addDays(today, days) : null,
      // Priced from the price list: the client can change rooms, how often and add-ons on the quote page.
      ...(answers ? { answers: { ...answers } } : {}),
      deposit:
        dep === "none" ? null : { kind: dep, value: dep === "amount" ? Math.round(Number(depValue) * 100) : Number(depValue) },
      lines: out.map((l, n) => ({
        description: l.description,
        quantity: l.quantity,
        unitPriceCents: l.unitPriceCents,
        note: l.note ?? null,
        link: l.link ?? null,
        optional: kept[n]?.optional ?? false,
        recurring: repeating && kept[n]?.frequency !== "first",
      })),
    };
    const done = {
      onSuccess: (r: QuoteDetail) => void navigate({ to: "/quotes/$id", params: { id: r.id } }),
      onError: (e: unknown) => setErr(errText(e)),
    };
    if (q) update.mutate({ id: q.id, patch: body }, done);
    else create.mutate({ ...body, ...(leadId ? { leadId } : {}) }, done);
  };
  const preview = quoteTotals(
    required.map((l) => ({ amountCents: draftTotal([l]), optional: false, picked: true })),
    dep === "none"
      ? null
      : { kind: dep, value: dep === "amount" ? Math.round(Number(depValue) * 100) || 0 : Number(depValue) || 0 },
  );
  const setLine = (next: DraftLine[], optional: boolean) =>
    setLines([...(optional ? required : []), ...next.map((l) => ({ ...l, optional })), ...(optional ? [] : extras)]);
  const pickedRow = (clients.data?.items ?? []).find((c) => c.id === who);
  return (
    <Page title={q ? `Edit quote #${String(q.number)}` : "New quote"} className="pform">
      <Card title="Service for">
        <div className="stack">
          <Combobox
            label="Client"
            value={who}
            onChange={(v) => {
              if (v !== NEW_CLIENT) setWho(v);
            }}
            options={(clients.data?.items ?? []).map((c) => ({
              value: c.id,
              label: c.name,
              sub: [c.address, [c.email, phoneText(c.phone)].filter(Boolean).join(" · ")].filter(Boolean).join("\n"),
              tag: c.active ? "Active" : "Inactive",
            }))}
            last={{
              value: NEW_CLIENT,
              label: "+ Create new client",
              onPick: (typed) => {
                const [first = "", ...rest] = typed.split(/\s+/);
                setNewClient({ firstName: first, lastName: rest.join(" ") });
              },
            }}
          />
          {who && (pickedRow || picked.data) ? (
            <div className="picked-client">
              <div className="who muted small">
                {(pickedRow?.address ?? picked.data?.address) ? <span>{pickedRow?.address ?? picked.data?.address}</span> : null}
                <span>
                  {[phoneText(pickedRow?.phone ?? picked.data?.phone ?? null), pickedRow?.email ?? picked.data?.email]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </div>
            </div>
          ) : !who && q ? (
            <div className="row">
              <p className="muted small">
                This quote is for {q.contactName ?? q.name}, who is not a client yet. Pick or make a client to link it.
              </p>
              <Button type="tertiary" size="small" icon="plus" onClick={makeContactClient}>
                Make them a client
              </Button>
            </div>
          ) : null}
        </div>
      </Card>
      {company.trade === "cleaning" ? (
        <QuickPrice size={size} start={savedHome} onUse={applyQuick} onHourly={(q) => applyQuick(q, null)} />
      ) : null}
      {company.trade === "lawn" ? (
        <LawnQuickPrice
          sqft={firstPlace?.lawnSqft}
          address={pickedRow?.address ?? picked.data?.address ?? q?.address ?? null}
          start={savedLawn}
          onUse={applyQuick}
        />
      ) : null}
      <Card title="The work">
        <div className="stack">
          <InputText
            label="Job title"
            value={title}
            placeholder="Move out clean, 3 bed 2 bath"
            onChange={(e) => setTitle(e.target.value)}
          />
          <InputText
            label="Message to the client"
            value={message}
            placeholder="Thanks for asking us. Here is what we would do."
            onChange={(e) => setMessage(e.target.value)}
          />
          <Choice
            label="How often"
            value={frequency}
            onChange={setFrequency}
            options={Object.entries(OFTEN_OF).map(([f, o]) => ({ value: f, label: PRICE_OFTEN_WORDS[o] }))}
          />
          <LinesEditor lines={required} onChange={(n) => setLine(n, false)} firstVisit={repeating} />
          {repeating ? (
            <p className="muted small">
              Every visit lines go on the job. First visit only lines are added to the first visit. The total is the first visit.
            </p>
          ) : null}
        </div>
      </Card>
      <Card title="Add-ons the client can pick">
        <p className="muted small">
          The client ticks the ones they want before they approve. They are not in the total until picked.
        </p>
        <LinesEditor lines={extras} onChange={(n) => setLine(n, true)} />
      </Card>
      <Card title="Deposit and how long it is good for">
        <div className="stack">
          <Choice<"none" | "percent" | "amount">
            label="Ask for a deposit when they approve"
            value={dep}
            onChange={setDep}
            options={[
              { value: "none", label: "No deposit" },
              { value: "percent", label: "Percent" },
              { value: "amount", label: "Amount" },
            ]}
          />
          {dep !== "none" ? (
            <InputText
              label={dep === "percent" ? "Percent of the total" : "Deposit amount"}
              inputMode="decimal"
              {...(dep === "percent" ? { suffix: "%" } : { prefix: "$" })}
              value={depValue}
              description={`About ${moneyExact(preview.depositCents)} of ${moneyExact(preview.totalCents)} before add-ons.`}
              onChange={(e) => setDepValue(e.target.value)}
            />
          ) : null}
          <InputText
            label="Good for"
            suffix="days"
            inputMode="numeric"
            value={goodFor}
            description={
              goodFor.trim() && Number(goodFor) > 0
                ? `The client can approve until ${shortDate(addDays(today, Number(goodFor) || 0))}. Leave it empty for no end.`
                : "No end. The client can approve any time."
            }
            onChange={(e) => setGoodFor(e.target.value)}
          />
        </div>
      </Card>
      {err ? <Banner type="error">{err}</Banner> : null}
      <div className="row end form-bar">
        <Button
          type="tertiary"
          onClick={() =>
            q ? void navigate({ to: "/quotes/$id", params: { id: q.id } }) : void navigate({ to: "/quotes", search: {} })
          }
        >
          Cancel
        </Button>
        <Button loading={create.isPending || update.isPending} onClick={save}>
          {q ? "Save changes" : "Save quote"}
        </Button>
      </div>
      <NewClientBox
        start={newClient}
        trade={company.trade}
        onClose={() => setNewClient(null)}
        onMade={(c) => {
          setNewClient(null);
          setWho(c.id);
          setErr(null);
        }}
      />
    </Page>
  );
}

// One quote ------------------------------------------------------------------------------------------------------------

/** What the New client box opens with: typed in the picker, or what a lead sent. */
export interface NewClientStart {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  source?: string;
}

/**
 * Jobber's New client box (owner, 2026-10-29: "more like Jobber's so auto fill works"). One field per thing with the
 * browser's autocomplete names, inside a real form, so Chrome fills the name, phone, email and the whole address at once.
 * Saving makes the client right away, as Jobber does, and picks them on the quote.
 */
export function NewClientBox({
  start,
  trade,
  onClose,
  onMade,
}: {
  start: NewClientStart | null;
  trade: string;
  onClose: () => void;
  onMade: (c: ClientDetail) => void;
}) {
  if (!start) return null;
  return <NewClientForm start={start} trade={trade} onClose={onClose} onMade={onMade} />;
}

function NewClientForm({
  start,
  trade,
  onClose,
  onMade,
}: {
  start: NewClientStart;
  trade: string;
  onClose: () => void;
  onMade: (c: ClientDetail) => void;
}) {
  const create = useCreateClient();
  const [f, setF] = useState({
    title: "",
    firstName: start.firstName ?? "",
    lastName: start.lastName ?? "",
    company: "",
    phone: start.phone ?? "",
    email: start.email ?? "",
    source: start.source ?? "",
  });
  const [where, setWhere] = useState<PlaceDraft>({
    ...EMPTY_PLACE,
    street1: start.street ?? "",
    street2: start.street2 ?? "",
    city: start.city ?? EMPTY_PLACE.city,
    state: start.state ?? EMPTY_PLACE.state,
    zip: start.zip ?? "",
  });
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => ({
    value: f[k],
    onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }),
  });
  const business = Boolean(f.company.trim());
  const place = placeProperty(where);
  const save = (e?: SyntheticEvent) => {
    e?.preventDefault();
    const missing = newClientMissing({ firstName: f.firstName, phone: f.phone }, { ...place, address: where.street1 });
    if (missing) {
      setErr(missing);
      return;
    }
    create.mutate(
      {
        ...(f.title ? { title: f.title as (typeof CLIENT_TITLES)[number] } : {}),
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim() || null,
        companyName: f.company.trim() || null,
        phone: f.phone.trim(),
        email: f.email.trim() || null,
        leadSource: f.source.trim() || null,
        property: propertyOut(place),
      },
      { onSuccess: onMade, onError: (x) => setErr(errText(x)) },
    );
  };
  return (
    <Modal
      title="New client"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={create.isPending} onClick={() => save()}>
            Save client
          </Button>
        </>
      }
    >
      <form className="client-form" autoComplete="on" onSubmit={save} aria-label="New client">
        <div className="span-2 wide-phone">
          <FormField label="Title">
            <select className="select" name="honorific-prefix" autoComplete="honorific-prefix" {...set("title")}>
              <option value="">No title</option>
              {CLIENT_TITLES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </FormField>
        </div>
        <div className="span-2 wide-phone">
          <InputText label="First name" name="given-name" autoComplete="given-name" required {...set("firstName")} />
        </div>
        <div className="span-2 wide-phone">
          <InputText label="Last name" name="family-name" autoComplete="family-name" {...set("lastName")} />
        </div>
        <div className="span-6">
          <InputText
            label="Company name"
            name="organization"
            autoComplete="organization"
            description="Only for a business."
            {...set("company")}
          />
        </div>
        <div className="span-3 wide-phone">
          <InputText label="Phone number" type="tel" name="tel" autoComplete="tel" required {...set("phone")} />
        </div>
        <div className="span-3 wide-phone">
          <InputText label="Email" type="email" name="email" autoComplete="email" {...set("email")} />
        </div>
        <div className="span-6">
          <InputText
            label="Lead source"
            name="lead-source"
            autoComplete="off"
            placeholder="Google, a friend, Facebook"
            {...set("source")}
          />
        </div>
        <PlaceFields p={where} set={setWhere} trade={trade} business={business} />
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        {err ? (
          <div className="span-6">
            <Banner type="error">{err}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}
